﻿<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
import { useContact } from '~/composables/useContact'
import { useFormations } from '~/composables/useFormations'
import { useSchool } from '~/composables/useSchool'
import { useDateFormat } from '~/composables/useDateFormat'
import { useNewsletter } from '~/composables/useNewsletter'
import { isValidEmail } from '~/utils/emailValidation'
import AppCertifications from '~/components/ui/AppCertifications.vue'

// SSR-safe: useState ensures same value on server and client hydration
const currentYear = useState('footer-year', () => useDateFormat().getYear(new Date()))
const { contactInfo } = useContact()
const { getAllFormations } = useFormations()
const { getMapsUrl } = useMaps()

const { data: formations } = await useAsyncData('footer-formations', () => getAllFormations())

// Dynamic Logos
const logoUrl = ref('')
const logoAlt = ref('OXO Logo')

// Branding (SSR)
const { getSchoolBranding } = useSchool()
const { data: branding } = await useAsyncData('footer-branding', () => getSchoolBranding())

if (branding.value) {
  if (branding.value.logo) logoUrl.value = branding.value.logo
  if (branding.value.logoAlt) logoAlt.value = branding.value.logoAlt
}

// Watch for changes (if navigating client-side)
watch(branding, (newVal) => {
  if (newVal) {
    if (newVal.logo) logoUrl.value = newVal.logo
    if (newVal.logoAlt) logoAlt.value = newVal.logoAlt
  }
})

// Helper for icons and colors
const SOLID_ICONS = new Set(['globe', 'link', 'store', 'gamepad'])

const getSocialIcon = (icon: string): string => {
  const name = (icon ?? 'link').startsWith('fa-') ? icon.slice(3) : (icon ?? 'link')
  return (SOLID_ICONS.has(name) ? 'fas' : 'fab') + ' fa-' + name
}

const getPlatformColor = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case 'discord': return '#5865F2'
    case 'linkedin': return '#0077B5'
    case 'instagram': return '#E4405F'
    case 'x': case 'twitter': return '#FFFFFF'
    case 'facebook': return '#1877F2'
    case 'youtube': return '#FF0000'
    case 'twitch': return '#9146FF'
    case 'tiktok': return '#000000'
    case 'github': return '#333333'
    case 'behance': return '#1769FF'
    case 'dribbble': return '#EA4C89'
    case 'pinterest': return '#BD081C'
    case 'snapchat': return '#FFFC00'
    case 'bluesky': return '#0085FF'
    default: return '#666666'
  }
}

// Newsletter
const { subscribe, isSubscribing: newsletterSubmitting, subscribeStatus: newsletterStatus } = useNewsletter()
const newsletterEmail = ref('')
const newsletterWebsite = ref('') // Honeypot
const newsletterFormLoadedAt = ref(0)

onMounted(() => {
  newsletterFormLoadedAt.value = Date.now()
})

const handleNewsletterSubmit = async () => {
  try {
    await subscribe(newsletterEmail.value, '', '', undefined, {
      website: newsletterWebsite.value,
      formLoadedAt: newsletterFormLoadedAt.value
    })
    newsletterEmail.value = ''
  } catch {
    // Error already handled by composable
  }
}

</script>

<template>
  <footer class="bg-background border-t border-oxo-border pt-24 pb-12 relative overflow-hidden transition-colors duration-300">
    <!-- Signature Background Statement - REMOVED -->


    <div class="max-w-7xl mx-auto px-6 relative z-10">
      <!-- Newsletter Signup -->
      <div class="mb-20">
        <ClientOnly>
          <div class="relative group">
            <!-- Premium Card Background -->
            <div class="absolute -inset-1 bg-linear-to-r from-oxo-red/20 via-oxo-orange/20 to-oxo-yellow/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            
            <div class="relative max-w-4xl mx-auto px-8 py-12 rounded-3xl bg-oxo-card-bg/40 backdrop-blur-xl border border-white/5 overflow-hidden">
              <!-- Cyber Grid Texture -->
              <div class="absolute inset-0 bg-cyber-grid opacity-[0.03] pointer-events-none"></div>
              
              <div class="relative z-10 max-w-2xl mx-auto text-center">
                <h3 class="font-display font-black text-[10px] uppercase tracking-[0.5em] text-oxo-red mb-4">Newsletter</h3>
                <h4 class="text-2xl md:text-3xl font-display font-black text-foreground mb-4 italic uppercase">Restez dans la <span class="text-oxo-red text-shadow-glow">course</span></h4>
                <p class="text-foreground-dim text-sm leading-relaxed mb-10 font-light max-w-md mx-auto">
                  Recevez les dernières actualités d'OXO : événements exclusifs, projets étudiants et opportunités de carrière.
                </p>
                
                <form @submit.prevent="handleNewsletterSubmit" class="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                  <!-- Honeypot (hidden from humans, bots will fill this) -->
                  <input
                    v-model="newsletterWebsite"
                    type="text"
                    name="website"
                    autocomplete="off"
                    tabindex="-1"
                    aria-hidden="true"
                    class="absolute left-[-9999px] opacity-0 pointer-events-none h-0 w-0"
                  />
                  <div class="flex-1 relative group/input">
                    <input
                      id="footer-newsletter-email"
                      v-model="newsletterEmail"
                      type="email"
                      name="email"
                      autocomplete="email"
                      required
                      placeholder="votre@email.com"
                      class="w-full px-6 py-4 rounded-2xl bg-white border border-white/10 text-black text-sm focus:outline-hidden focus:border-oxo-red/50 transition-all duration-500 shadow-inner"
                      :class="{ 'border-red-500/50!': newsletterEmail && !isValidEmail(newsletterEmail), 'border-green-500/50!': newsletterEmail && isValidEmail(newsletterEmail) }"
                    />
                    <div class="absolute inset-x-6 bottom-0 h-px bg-linear-to-r from-transparent via-oxo-red/50 to-transparent scale-x-0 group-focus-within/input:scale-x-100 transition-transform duration-700"></div>
                  </div>
                  
                  <button
                    type="submit"
                    :disabled="newsletterSubmitting || !newsletterEmail || !isValidEmail(newsletterEmail)"
                    class="relative group/btn px-8 py-4 bg-linear-to-br from-oxo-red to-oxo-orange text-white text-xs font-black uppercase tracking-widest rounded-2xl transition-all duration-500 disabled:opacity-50 disabled:grayscale overflow-hidden shadow-[0_10px_20px_-10px_rgba(var(--oxo-red-rgb),0.5)] hover:shadow-[0_15px_30px_-10px_rgba(var(--oxo-red-rgb),0.6)] hover:-translate-y-1 active:scale-95"
                  >
                    <span class="relative z-10 flex items-center justify-center gap-2">
                       <i v-if="newsletterSubmitting" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                       <span v-else>S'inscrire</span>
                       <i class="fas fa-chevron-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                    </span>
                    <!-- Shine Effect -->
                    <div class="absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-[shimmer_1.5s_infinite] skew-x-12"></div>
                  </button>
                </form>

                <p v-if="newsletterStatus" :class="`text-xs mt-4 font-bold tracking-wide uppercase ${newsletterStatus.success ? 'text-green-400' : 'text-red-400'}`">
                  {{ newsletterStatus.message }}
                </p>
                
                <div class="mt-8 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-center gap-4">
                   <p class="text-[10px] text-foreground-dim/40 uppercase tracking-widest leading-relaxed">
                     En vous inscrivant, vous acceptez notre <NuxtLink to="/politique-confidentialite" class="text-oxo-red hover:underline">politique de confidentialité</NuxtLink>
                   </p>
                </div>
              </div>
            </div>
          </div>

          <template #fallback>
            <div class="max-w-2xl mx-auto text-center opacity-50 grayscale">
              <h3 class="font-display font-black text-xs uppercase tracking-[0.3em] text-foreground-dim/40 mb-4">Newsletter</h3>
              <div class="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div class="flex-1 px-5 py-3 rounded-xl bg-background/20 border border-white/5"></div>
                <div class="px-6 py-3 bg-oxo-red/20 rounded-xl w-32"></div>
              </div>
            </div>
          </template>
        </ClientOnly>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-16 mb-20">
        
        <!-- Brand Column -->
        <div class="col-span-2 md:col-span-1 flex flex-col items-center md:items-start text-center md:text-left">
          <NuxtLink to="/" class="block mb-8 group">
              <template v-if="logoUrl">
                  <NuxtImg loading="lazy" :src="logoUrl" :alt="logoAlt" width="144" height="48" class="h-12 w-36 object-contain md:object-left group-hover:scale-105 transition-transform duration-500" sizes="144px" />
              </template>
              <div v-else class="text-4xl font-black font-display group-hover:scale-105 transition-transform duration-500 origin-center md:origin-left text-foreground">OXO<span class="text-oxo-red">.</span></div>
          </NuxtLink>
          <p class="text-foreground-dim text-sm leading-relaxed mb-8 max-w-xs mx-auto md:mx-0 font-light">
            L'école supérieure des métiers du jeu vidéo. <br class="hidden md:block">
            Développez votre talent, dessinez votre avenir.
          </p>
          <div class="flex gap-4 flex-wrap justify-center md:justify-start">
            <a 
              v-for="social in contactInfo.socials" 
              :key="social.platform"
              :href="social.url" 
              target="_blank" 
              class="social-link w-11 h-11 bg-bacground-dim/20 border border-oxo-border rounded-2xl flex items-center justify-center transition-all duration-500 text-foreground-dim hover:text-white"
              :style="{ '--hover-color': getPlatformColor(social.platform) }"
              :data-umami-event="`Footer - Click ${social.platform}`"
              :aria-label="`Suivez-nous sur ${social.platform}`"
            >
              <i :style="faIconStyle(getSocialIcon(social.icon))" :class="getSocialIcon(social.icon) + ' text-lg'" aria-hidden="true"></i>
            </a>
          </div>
        </div>

        <!-- Links Column 1: L'École -->
        <nav class="col-span-1" aria-labelledby="footer-school-heading">
          <h3 id="footer-school-heading" class="font-display font-black text-xs uppercase tracking-[0.3em] text-foreground-dim/40 mb-8">L'École</h3>
          <ul class="space-y-4 text-sm font-light">
            <li><NuxtLink to="/ecole#pedagogie" class="text-foreground-dim hover:text-oxo-red transition-all duration-300 block">La Pédagogie</NuxtLink></li>
            <li><NuxtLink to="/ecole#equipe" class="text-foreground-dim hover:text-oxo-red transition-all duration-300 block">Notre Équipe</NuxtLink></li>
            <li><NuxtLink to="/#partners" class="text-foreground-dim hover:text-oxo-red transition-all duration-300 block">Nos Partenaires</NuxtLink></li>
          </ul>
        </nav>

        <!-- Links Column 2: Formations -->
        <nav class="col-span-1" aria-labelledby="footer-formations-heading">
          <h3 id="footer-formations-heading" class="font-display font-black text-xs uppercase tracking-[0.3em] text-foreground-dim/40 mb-8">Formations</h3>
          <ul class="space-y-4 text-sm font-light">
            <li v-for="fmt in formations?.slice(0, 4)" :key="fmt.slug">
               <NuxtLink :to="`/formations/${fmt.slug}`" class="text-foreground-dim hover:text-oxo-red transition-all duration-300 block">{{ fmt.title }}</NuxtLink>
            </li>
            <li v-if="formations && formations.length > 4" class="text-[10px] text-foreground-dim uppercase tracking-widest pt-2">Plus prochainement</li>
          </ul>
        </nav>


        <!-- Contact Column -->
        <div class="col-span-2 md:col-span-1 flex flex-col items-center md:items-start text-center md:text-left">
          <h3 class="font-display font-black text-xs uppercase tracking-[0.3em] text-foreground-dim/40 mb-8">Contact</h3>
          <ul class="space-y-6 text-sm font-light w-full">
            <li class="flex flex-col md:flex-row items-center md:items-start gap-4 group">
              <div class="w-10 h-10 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center shrink-0 group-hover:bg-oxo-red/10 group-hover:border-oxo-red/20 transition-all duration-500">
                 <i class="fas fa-map-marker-alt text-foreground-dim/60 group-hover:text-oxo-red transition-colors" aria-hidden="true"></i>
              </div>
              <a 
                v-if="contactInfo.address"
                :href="getMapsUrl(contactInfo.address, 'OXO')"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 pt-1 md:pt-0.5 text-foreground-dim group-hover:text-foreground hover:text-oxo-red transition-colors duration-300"
              >
                {{ contactInfo.address }}
              </a>
            </li>
            <li class="flex flex-col md:flex-row items-center md:items-start gap-4 group">
              <div class="w-10 h-10 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center shrink-0 group-hover:bg-oxo-red/10 group-hover:border-oxo-red/20 transition-all duration-500">
                 <i class="fas fa-envelope text-foreground-dim/60 group-hover:text-oxo-red transition-colors" aria-hidden="true"></i>
              </div>
              <a :href="`mailto:${contactInfo.email}`" class="flex-1 pt-1 md:pt-0.5 text-foreground-dim hover:text-oxo-red transition-colors break-all">{{ contactInfo.email }}</a>
            </li>
            <li class="flex flex-col md:flex-row items-center md:items-start gap-4 group">
              <div class="w-10 h-10 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center shrink-0 group-hover:bg-oxo-red/10 group-hover:border-oxo-red/20 transition-all duration-500">
                 <i class="fas fa-phone text-foreground-dim/60 group-hover:text-oxo-red transition-colors" aria-hidden="true"></i>
              </div>
              <a :href="`tel:${contactInfo.phone}`" class="flex-1 pt-1 md:pt-0.5 text-foreground-dim hover:text-oxo-red transition-colors">{{ contactInfo.phone }}</a>
            </li>
          </ul>
        </div>
      </div>



      <!-- Certifications -->
      <div class="py-6 border-t border-oxo-border flex flex-col items-start gap-4">
        <span class="font-display font-black text-[10px] uppercase tracking-[0.3em] text-foreground-dim/40 whitespace-nowrap">Accréditations &amp; Qualité</span>
        <AppCertifications compact />
      </div>

      <!-- Bottom Bar -->
      <div class="pt-8 border-t border-oxo-border flex flex-col md:flex-row justify-between items-center gap-8">
        <p class="text-[11px] text-foreground-dim tracking-wider">
          &copy; {{ currentYear }} OXO — <span class="italic">Tous droits réservés.</span>
        </p>
        <div class="flex flex-wrap justify-center gap-x-8 gap-y-4 text-[11px] font-medium tracking-widest text-foreground-dim/60 uppercase">
          <NuxtLink to="/mentions-legales" class="hover:text-foreground transition-colors duration-300">Mentions</NuxtLink>
          <NuxtLink to="/politique-confidentialite" class="hover:text-foreground transition-colors duration-300">Confidentialité</NuxtLink>
          <NuxtLink to="/cgu" class="hover:text-foreground transition-colors duration-300">CGU</NuxtLink>
          <NuxtLink to="/cgv" class="hover:text-foreground transition-colors duration-300">CGV</NuxtLink>
          <NuxtLink to="/sitemap" class="hover:text-foreground transition-colors duration-300">Sitemap</NuxtLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.social-link:hover {
  background-color: var(--hover-color);
  color: white;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.text-shadow-glow {
  text-shadow: 0 0 10px rgba(var(--oxo-red-rgb), 0.5);
}
</style>
